import {bankingRequest} from '../services/request';
import {lastTransactionStatusConstants, transactionConstants} from '../constants';
import {DEFAULT_CURRENCY_CODE} from '../../constants/amount.constants';
import {bankingEndpoints} from '../../api';
import {apiHelpers, objectHelpers} from '../../utils/helpers';

const getUrl = apiHelpers.getUrl;

const errorHandlerProps = {disableAlert: true};

const POLL_INTERVAL_MS = 3000;

const pollBackgroundJob = ({ jobId, headers, dispatch, successCallback, errorCallback, isCancelled }) => {
  const doPoll = () => {
    if (isCancelled?.()) return;

    bankingRequest.get({
      dispatch,
      headers,
      url: getUrl(bankingEndpoints.GET_BACKGROUND_JOB, { jobId }),
      errorHandlerProps,
      successCallback: (data) => {
        if (isCancelled?.()) return;
        const { status, result } = data;

        if (status === 'completed') {
          successCallback?.(result);
        } else if (status === 'failed') {
          errorCallback?.({ response: { status: 400 } });
        } else {
          setTimeout(doPoll, POLL_INTERVAL_MS);
        }
      },
      errorCallback: (err) => {
        if (isCancelled()) return;
        errorCallback?.(err);
      },
    });
  };

  doPoll();
};

// `skipPolling`: report success as soon as the job is enqueued instead of waiting for it to finish
// (the file then arrives by notification and email).
const downloadWithBackgroundJob = ({ url, headers, query, dispatch, successCallback, errorCallback, skipPolling = false }) => {
  let cancelled = false;

  bankingRequest.get({
    dispatch,
    headers,
    url,
    query,
    errorHandlerProps,
    successCallback: (data) => {
      if (!data.id || skipPolling) {
        successCallback?.(data);
        return;
      }
      pollBackgroundJob({
        jobId: data.id,
        headers,
        dispatch,
        successCallback,
        errorCallback,
        isCancelled: () => cancelled,
      });
    },
    errorCallback,
  });

  return () => { cancelled = true; };
};

const getTransactionsList = ({
  headers,
  query,
  successCallback,
  errorCallback
}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      headers,
      url: bankingEndpoints.GET_TRANSACTIONS,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getUserTransactionsList = ({
  headers,
  userId,
  query,
  successCallback,
  errorCallback
}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      headers,
      url: getUrl(bankingEndpoints.GET_USER_TRANSACTIONS, {userId}),
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getTransactionsTotals = ({
  headers,
  query,
  successCallback,
  errorCallback
}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      headers,
      url: bankingEndpoints.GET_TRANSACTIONS_TOTALS,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}


// Polls an export job the caller already has the id for (the emailed link, plan
// ready-to-use-accounting-exports.md §5 story 3b) -- unlike downloadWithBackgroundJob,
// there is no enqueue call first.
const getBackgroundJob = ({ jobId, headers, successCallback, errorCallback }) => {
  return (dispatch) => {
    let cancelled = false;

    pollBackgroundJob({
      jobId,
      headers,
      dispatch,
      successCallback,
      errorCallback,
      isCancelled: () => cancelled,
    });

    return () => { cancelled = true; };
  };
};

const downloadTransactions = ({ headers, query, skipPolling, successCallback, errorCallback }) => {
  return (dispatch) => downloadWithBackgroundJob({
    skipPolling,
    url: bankingEndpoints.GET_TRANSACTIONS_DOWNLOAD,
    headers,
    query,
    dispatch,
    successCallback,
    errorCallback,
  });
};

const downloadUserTransactions = ({ headers, query, userId, successCallback, errorCallback }) => {
  return (dispatch) => downloadWithBackgroundJob({
    url: getUrl(bankingEndpoints.GET_USER_TRANSACTIONS_DOWNLOAD, { userId }),
    headers,
    query,
    dispatch,
    successCallback,
    errorCallback,
  });
};

const getSubscriptionTransactionsList = ({
  headers,
  id,
  query,
  successCallback,
  errorCallback
}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      headers,
      url: getUrl(bankingEndpoints.GET_SUBSCRIPTION_TRANSACTIONS, {id}),
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const getUserCardTransactionsList = ({
  cardId,
  userId,
  headers,
  query,
  successCallback,
  errorCallback
}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      headers,
      url: getUrl(bankingEndpoints.GET_USER_CARD_TRANSACTIONS, {cardId, userId}),
      query,
      successCallback,
      errorCallback,
      errorHandlerProps
    });
  }
}

const storeBalanceDetails = (balance, isLoaded = false) => {
  return (dispatch) => {
    dispatch({
      type: transactionConstants.SET_BALANCE_DETAILS,
      data: {
        currency: objectHelpers.getObjProp(balance, 'currency', DEFAULT_CURRENCY_CODE),
        value: objectHelpers.getObjProp(balance, 'authorized_balance', 0),
        isLoaded,
        isRequested: true
      }
    });
  }
}

const storeWireDetails = (wallet, isLoaded = false) => {
  return (dispatch) => {
    dispatch({
      type: transactionConstants.SET_WIRE_DETAILS,
      data: {
        bic: objectHelpers.getObjProp(wallet, 'bic'),
        creationDate: objectHelpers.getObjProp(wallet, 'created_at'),
        iban: objectHelpers.getObjProp(wallet, 'iban'),
        walletId: objectHelpers.getObjProp(wallet, 'id'),
        isLoaded
      }
    });
  }
}

const storeBankingUserId = (id) => {
  return (dispatch) => {
    dispatch({
      type: transactionConstants.SET_COMPANY_BANKING_USER_ID,
      data: {id}
    });
  }
}

const openAddInvoiceModal = (transaction) => {
  return (dispatch) => {
    dispatch({
      type: transactionConstants.OPEN_ADD_INVOICE_MODAL,
      data: {
        open: true,
        transaction
      }
    });
  }
}

const closeAddInvoiceModal = () => {
  return (dispatch) => {
    dispatch({
      type: transactionConstants.CLOSE_ADD_INVOICE_MODAL
    });
  }
}

const clearBankingDetails = () => {
  return {
    type: transactionConstants.CLEAR_TRANSACTIONS_BANKING_DETAILS
  }
}

const getLastTransaction = ({ successCallback, errorCallback } = {}) => {
  return (dispatch, getState) => {
    // A response for a company that has since been switched away from must not land in the new one's
    // state: it would mark the new company's status loaded with the old company's date.
    const companyId = getState().company?.id;
    const isStillCurrentCompany = () => getState().company?.id === companyId;

    bankingRequest.get({
      dispatch,
      url: bankingEndpoints.GET_LAST_TRANSACTION,
      successCallback: (data) => {
        if (!isStillCurrentCompany()) return;
        dispatch({
          type: transactionConstants.GET_LAST_TRANSACTION,
          data
        });
        successCallback && successCallback(data);
      },
      errorCallback: (response) => {
        if (isStillCurrentCompany()) dispatch({type: transactionConstants.GET_LAST_TRANSACTION_FAILED});
        errorCallback && errorCallback(response);
      },
      // `successHandler` calls neither callback for a 200 body carrying an `errors` key; without this
      // the status would stay idle and the Transactions first load would wait on it forever.
      finallyCallback: () => {
        const {lastTransactionStatus} = getState().transaction;
        if (isStillCurrentCompany() && lastTransactionStatus === lastTransactionStatusConstants.IDLE) {
          dispatch({type: transactionConstants.GET_LAST_TRANSACTION_FAILED});
        }
      },
      errorHandlerProps
    });
  }
}

const downloadVendorTransactions = ({ id, headers, query, successCallback, errorCallback }) => {
  return (dispatch) => downloadWithBackgroundJob({
    url: getUrl(bankingEndpoints.GET_VENDOR_TRANSACTIONS_DOWNLOAD, { id }),
    headers,
    query,
    dispatch,
    successCallback,
    errorCallback,
  });
};

const getCashflowMonthly = ({query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      url: bankingEndpoints.GET_BANKING_CASHFLOW_MONTHLY,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

// The Documents tile's recap line (plan ready-to-use-accounting-exports.md §5 story 7, "The
// Documents recap"). The endpoint ships in story 8 -- until then this 404s on every call, and
// `errorHandlerProps` keeps that silent: the recap is informational and must not block a download.
const getTransactionsDocumentsSummary = ({query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    bankingRequest.get({
      dispatch,
      url: bankingEndpoints.GET_TRANSACTIONS_DOCUMENTS_SUMMARY,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

export const transactionActions = {
  clearBankingDetails,
  getTransactionsList,
  getTransactionsTotals,
  downloadTransactions,
  downloadUserTransactions,
  getBackgroundJob,
  getSubscriptionTransactionsList,
  getUserCardTransactionsList,
  getUserTransactionsList,
  storeBalanceDetails,
  storeWireDetails,
  storeBankingUserId,

  openAddInvoiceModal,
  closeAddInvoiceModal,

  getLastTransaction,

  downloadVendorTransactions,
  getCashflowMonthly,
  getTransactionsDocumentsSummary,
};
